/**
 * The report dialog (REPORT-SPEC §5.2–§5.5).
 *
 * The first `<dialog>` in this codebase, which is why the accessibility
 * decisions are written down rather than assumed. Native `<dialog>` supplies
 * modal focus containment and Escape; it does **not** supply focus return, a
 * live region, or an initial focus target that is not the first focusable
 * element — so those are here, and §5.5 asks for each of them by name.
 *
 * ## What this module may not touch
 *
 * §5.4: "Submitting and all failures are isolated from the model state. They
 * may not re-render the map, change its selected metric, scroll it, alter the
 * URL, or clear a selected tensor."
 *
 * So this owns its own DOM subtree and nothing else. It reads a snapshot of the
 * view when it opens and never reads again — which also means a metric change
 * behind the dialog cannot silently alter what a half-written report claims to
 * be about.
 *
 * ## The submission id, and why it lives in a closure
 *
 * §5.4: one `crypto.randomUUID()` per fresh form, kept in memory for retries of
 * that form, replaced after a success, and never written to storage, a cookie
 * or the URL. A module-level variable would survive a closed dialog; a field on
 * the element would be readable from the page. A closure is the narrowest thing
 * that outlives a retry and nothing else.
 */

import { escapeXml as escapeHtml } from "../render/card-map.js";
import {
  BROAD_PROBLEM_TYPE,
  LIMITS,
  MAX_PROBLEM_TYPES,
  PROBLEM_TYPES,
  type ProblemType,
  SCHEMA_VERSION,
  canonicalProblemTypes,
} from "../report/contract.js";
import type { ReportContext } from "../report/contract.js";
import { normalizeDetails } from "../report/normalize.js";
import { type SubmitResult, submitReport } from "../report/submit.js";

/** §5.2's labels, in the enum's canonical order. */
const LABELS: Record<ProblemType, string> = {
  looks_wrong: "It just looks wrong (picking only this is fine)",
  anatomy_map_wrong: "The anatomy map is rendered or structured incorrectly",
  tensor_group_missing: "An important tensor group is missing",
  tensor_group_wrong: "Tensors are assigned to the wrong group",
  parameter_count_wrong: "The parameter count is wrong",
  byte_count_wrong: "The byte count or model size is wrong",
  bpw_wrong: "Bits per weight (BPW) is wrong",
  dtype_quantization_wrong: "Storage type or quantization is wrong",
  tensor_detail_wrong: "A tensor detail or statistic is wrong",
  comparison_wrong: "The model comparison is wrong",
  other: "Another TensorLens result is wrong",
};

export interface ReportSubject {
  readonly repoId: string;
  readonly revisionSha: string;
  readonly context: ReportContext;
}

/** Everything the dialog needs, so it can be driven without a page. */
export interface DialogDeps {
  /** Injected so a test does not depend on `crypto.randomUUID` being present. */
  readonly newId: () => string;
  readonly send: (report: {
    schemaVersion: typeof SCHEMA_VERSION;
    submissionId: string;
    repoId: string;
    revisionSha: string;
    problemTypes: readonly ProblemType[];
    details?: string;
    context: ReportContext;
  }) => Promise<SubmitResult>;
}

const DEFAULT_DEPS: DialogDeps = {
  newId: () => crypto.randomUUID(),
  send: submitReport,
};

function markup(subject: ReportSubject): string {
  const boxes = PROBLEM_TYPES.map(
    (type) => `<label class="report-choice">
      <input type="checkbox" name="problemType" value="${type}" />
      <span>${escapeHtml(LABELS[type])}</span>
    </label>`,
  ).join("");

  return `<form method="dialog" class="report-form" novalidate>
    <h2 id="report-title">Report a TensorLens issue</h2>
    <p class="report-status" id="report-status" role="status" aria-live="polite"></p>
    <p class="report-thanks" id="report-thanks">Thank you! \u2764\uFE0F</p>
    <p class="prose prose--muted report-aftermath" id="report-aftermath"></p>
    <p class="prose prose--muted" id="report-intro">
      Tell us what TensorLens displays or calculates incorrectly for this model.
      The exact model revision and current view will be included automatically.
    </p>
    <p class="report-subject mono">${escapeHtml(subject.repoId)}@${escapeHtml(
      subject.revisionSha.slice(0, 7),
    )}</p>
    <p class="prose prose--muted">
      We can't promise every issue can be resolved, but these reports are read
      regularly and we'll do our best.
    </p>

    <fieldset class="report-types" aria-describedby="report-types-error">
      <legend>
        What seems wrong? Select all that apply.
        <span class="report-required" id="report-required" hidden>(pick at least one)</span>
      </legend>
      <div class="report-choices">${boxes}</div>
      <p class="report-error" id="report-types-error" role="status"></p>
    </fieldset>

    <label class="report-details-label" for="report-details">Add details (optional)</label>
    <textarea id="report-details" class="report-details" rows="5"
      maxlength="${LIMITS.details}"
      aria-describedby="report-details-hint report-counter report-details-error"></textarea>
    <p class="prose prose--muted" id="report-details-hint">
      What you saw, and what you expected. For example: “Layer 0 attention
      parameters are counted twice; the Hub tensors only contain one set.”
    </p>
    <p class="report-counter" id="report-counter">0 / ${LIMITS.details}</p>
    <p class="report-error" id="report-details-error" role="status"></p>

    <p class="prose prose--muted report-privacy">
      Please don't include secrets or personal information. Reports are stored
      privately for TensorLens maintenance. We cannot reply from this form.
    </p>

    <p class="report-alert" id="report-alert" role="alert"></p>

    <div class="report-actions">
      <button type="button" class="btn" data-report-cancel>Cancel</button>
      <button type="button" class="btn btn--primary" data-report-send disabled>Send report</button>
    </div>
  </form>`;
}

/**
 * Open the dialog for one model.
 *
 * `opener` is where focus returns. §5.5 requires it and native `<dialog>` does
 * not do it: closing returns focus to whatever the browser last considered
 * focused, which after a click is often the body.
 */
export function openReportDialog(
  subject: ReportSubject,
  opener: HTMLElement,
  deps: DialogDeps = DEFAULT_DEPS,
): HTMLDialogElement {
  const dialog = document.createElement("dialog");
  dialog.className = "report-dialog";
  dialog.setAttribute("aria-labelledby", "report-title");
  dialog.setAttribute("aria-describedby", "report-intro");
  dialog.innerHTML = markup(subject);
  document.body.append(dialog);

  const q = <T extends HTMLElement>(selector: string): T =>
    dialog.querySelector(selector) as T;
  const boxes = [
    ...dialog.querySelectorAll<HTMLInputElement>('input[name="problemType"]'),
  ];
  const details = q<HTMLTextAreaElement>("#report-details");
  const counter = q<HTMLParagraphElement>("#report-counter");
  const typesError = q<HTMLParagraphElement>("#report-types-error");
  const detailsError = q<HTMLParagraphElement>("#report-details-error");
  const status = q<HTMLParagraphElement>("#report-status");
  const aftermath = q<HTMLParagraphElement>("#report-aftermath");
  const alert = q<HTMLParagraphElement>("#report-alert");
  const form = q<HTMLFormElement>(".report-form");
  const send = q<HTMLButtonElement>("[data-report-send]");
  const required = q<HTMLSpanElement>("#report-required");
  const cancel = q<HTMLButtonElement>("[data-report-cancel]");

  /** §5.4: one id per fresh form, replaced only after a success. */
  let submissionId = deps.newId();
  let sending = false;

  const selected = (): ProblemType[] =>
    boxes.filter((box) => box.checked).map((box) => box.value as ProblemType);

  /**
   * §5.2's two constraints, applied to the boxes themselves.
   *
   * The broad flag is mutually exclusive with the specifics — selecting one
   * clears the other — and past four specifics the rest are disabled with a
   * visible hint. Disabling rather than silently ignoring a fifth click: a
   * checkbox that ticks and then does nothing is worse than one that cannot be
   * ticked, because only the second says why.
   */
  function applyExclusivity(changed: HTMLInputElement): void {
    if (changed.checked) {
      if (changed.value === BROAD_PROBLEM_TYPE) {
        for (const box of boxes) {
          if (box.value !== BROAD_PROBLEM_TYPE) box.checked = false;
        }
      } else {
        const broad = boxes.find((box) => box.value === BROAD_PROBLEM_TYPE);
        if (broad) broad.checked = false;
      }
    }

    const chosen = selected();
    const atLimit =
      chosen.length >= MAX_PROBLEM_TYPES && !chosen.includes(BROAD_PROBLEM_TYPE);
    for (const box of boxes) {
      box.disabled = sending || (atLimit && !box.checked);
    }
    typesError.textContent = atLimit
      ? `Choose up to ${MAX_PROBLEM_TYPES}. Uncheck one to pick another.`
      : "";
  }

  /** The details field's own validity, reported the way §5.5 asks. */
  function validateDetails(): boolean {
    const value = details.value;
    if (value === "") {
      detailsError.textContent = "";
      details.removeAttribute("aria-invalid");
      return true;
    }
    const normalized = normalizeDetails(value, LIMITS.details);
    if (normalized.ok) {
      detailsError.textContent = "";
      details.removeAttribute("aria-invalid");
      return true;
    }
    details.setAttribute("aria-invalid", "true");
    detailsError.textContent =
      normalized.reason === "too_long"
        ? `Details must be ${LIMITS.details} characters or fewer.`
        : "Details contain a character that cannot be stored. Remove any formatting and try again.";
    return false;
  }

  function refresh(): void {
    counter.textContent = `${details.value.length} / ${LIMITS.details}`;
    const chosen = selected();
    send.disabled =
      sending ||
      chosen.length === 0 ||
      chosen.length > MAX_PROBLEM_TYPES ||
      !validateDetails();

    /*
     * The stuck case: someone writes out a careful paragraph, presses Send, and
     * nothing happens because a checkbox above is unticked. A bad experience,
     * and it loses the report.
     *
     * **Why not auto-select `looks_wrong`.** §5.2 forbids it — "the visitor
     * makes the assertion" — and the reason survives inspection: §11.4.1 makes
     * several `looks_wrong` reports on one SHA the trigger for a trusted
     * reproduction pass, so a flag nobody ticked would inflate exactly the
     * signal it is read as. It is also still a claim, about the reporter rather
     * than the model, and not ours to make for them.
     *
     * **Why it is three words in the legend** rather than a prompt near the
     * button (owner, 2026-08-11). A block that appears at the bottom resizes a
     * dialog that is already tall, and a form that grows while you are typing
     * in it moves the thing you were reading. Amber text where the requirement
     * is stated is noticed because it is the only colour in the dialog, and it
     * costs no layout at all.
     *
     * Only once there is something to lose — on a fresh form nothing is
     * expected yet, and a permanent amber note would be the caution state the
     * token exists to avoid.
     */
    required.hidden = sending || chosen.length > 0 || details.value.trim() === "";
  }

  function setSending(active: boolean): void {
    sending = active;
    send.disabled = active;
    send.textContent = active ? "Sending…" : "Send report";
    cancel.disabled = active;
    details.disabled = active;
    for (const box of boxes) box.disabled = active;
    if (!active) refresh();
  }

  /**
   * Close, detach, and hand focus back — **without depending on the `close`
   * event**.
   *
   * Measured in Chrome 150 on 2026-08-12: a **scripted** `close()` flips `open`
   * from true to false and fires no `close` event at all. The cleanup below was
   * hung entirely off that listener, so for every dismissal the page itself
   * initiates it never ran — and the failure is precisely the one the
   * listener's own comment predicts. Opening and dismissing the dialog four
   * times leaves four of them in the document, four elements answering to
   * `#report-details`, and `document.querySelector("#report-details")`
   * resolving to one inside a *closed* dialog.
   *
   * **Escape was never affected**, and the distinction is worth keeping: a
   * user-initiated dismissal goes through the UA's own path and does fire
   * `close`. Only the scripted route is silent. Confirmed on production — the
   * button was broken, Escape was not — after an automated check wrongly said
   * otherwise, because a synthesized Escape does not dismiss a `<dialog>` at
   * all and was measuring one that had never closed.
   *
   * So the work happens where the dismissal is initiated, and the listener
   * stays for the routes the page does not initiate.
   *
   * **The guard is load-bearing, not defensive.** Both routes can run for one
   * dismissal, and this first relied on `close()` being a no-op on an already
   * closed dialog to make that harmless. That is true of the real API and it is
   * not something to depend on: a `close()` that dispatches unconditionally
   * turns listener-calls-close into unbounded recursion, which is exactly what
   * CI hit. A latch is the thing that cannot be got wrong by an environment.
   */
  let dismissed = false;
  function close(): void {
    if (dismissed) return;
    dismissed = true;
    dialog.close();
    if (dialog.isConnected) dialog.remove();
    if (opener.isConnected && !(opener as HTMLButtonElement).disabled) {
      opener.focus();
    }
  }

  /**
   * One attempt, plus §5.4's single retry on a 409.
   *
   * The retry is not a loop. A conflict means either a client defect or forged
   * traffic — never normal use — so retrying forever would turn a bug into
   * load. One retry with a fresh id recovers the visitor's report; the
   * server-side metric is what makes the cause visible (§14.2).
   */
  async function attempt(retriedOnce: boolean): Promise<void> {
    const normalized = normalizeDetails(details.value, LIMITS.details);
    const text =
      normalized.ok && normalized.value !== "" ? normalized.value : undefined;

    const result = await deps.send({
      schemaVersion: SCHEMA_VERSION,
      submissionId,
      repoId: subject.repoId,
      revisionSha: subject.revisionSha,
      problemTypes: canonicalProblemTypes(selected()),
      ...(text !== undefined ? { details: text } : {}),
      context: subject.context,
    });

    if (result.kind === "sent") {
      /*
       * §5.4's success state. The form is replaced rather than left behind a
       * message: a Send button still on screen after a stored report is an
       * invitation to file it twice.
       */
      status.textContent = result.reportId
        ? `Report received. Your receipt is ${result.reportId.slice(0, 8)}.`
        : "Report received.";
      /*
       * What happens next, stated honestly (owner, 2026-08-11).
       *
       * The wording is careful in two places, because §4 rules out "a
       * service-level promise that every report receives a reply or is fixed"
       * and §12.3 rules out promising a process that does not exist.
       *
       * "Read every few days" is a statement of practice, not an undertaking to
       * this person. And "you'll see it on this page" is the only true form of
       * *check back*: there is no reply, no status endpoint and no receipt
       * lookup — v1 stores no reporter identity, so there is nothing to look
       * anything up by. Saying "check back" without saying where would send
       * someone hunting for a status page that will never exist.
       */
      aftermath.textContent =
        "There's no reply from this form and no status to follow. Reports are " +
        "read every few days — if this one leads to a fix, you'll see it here " +
        "on the model page.";
      alert.textContent = "";
      /*
       * One class on the form, not `hidden` on six elements.
       *
       * `hidden` was the first attempt and **silently did nothing** to half of
       * them: the attribute works by a UA rule setting `display: none`, and any
       * author `display` wins over it. `.report-details-label` is `display:
       * block` and `.btn` sets its own — so the label and a "Sending…" button
       * both stayed on screen after a successful send, which is exactly what a
       * visitor should not see. A state class puts the rule in author CSS at
       * the same specificity as everything it is fighting.
       */
      form.classList.add("report-form--sent");
      status.classList.add("report-status--sent");
      cancel.textContent = "Close";
      cancel.disabled = false;
      // §5.4: the next opening gets a new id.
      submissionId = "";
      return;
    }

    if (result.kind === "conflict" && !retriedOnce) {
      submissionId = deps.newId();
      await attempt(true);
      return;
    }

    setSending(false);
    // §5.4's messages: useful, non-technical, and the entered text is kept.
    alert.textContent =
      result.kind === "rate-limited"
        ? "Too many reports were sent from this network. Try again later."
        : result.kind === "too-large"
          ? "That report is too large."
          : result.kind === "invalid"
            ? "That report could not be accepted. Please check the details and try again."
            : "The report could not be saved. Try again.";
    status.textContent = "";
  }

  for (const box of boxes) {
    box.addEventListener("change", () => {
      applyExclusivity(box);
      refresh();
    });
  }
  details.addEventListener("input", refresh);

  send.addEventListener("click", () => {
    // §5.5: a second click or Enter while sending cannot create a second
    // request. The guard is the flag, not the disabled attribute, because a
    // synthetic click bypasses the latter.
    if (sending || send.disabled) return;
    setSending(true);
    status.textContent = "Sending your report…";
    alert.textContent = "";
    void attempt(false);
  });

  cancel.addEventListener("click", close);

  /*
   * Focus returns to the button that opened this. §5.5 requires it and native
   * `<dialog>` does not do it — after a click the browser's idea of the
   * previously focused element is often the body, which drops a keyboard user
   * at the top of the page.
   *
   * The dialog also removes itself. Left in the DOM it would accumulate one
   * hidden form per open, each with its own listeners and its own submission
   * id — and `document.querySelector("#report-details")` would start finding
   * the wrong one.
   */
  dialog.addEventListener("close", close);

  dialog.showModal();
  refresh();
  // §5.5: the first problem checkbox, not the destructive edge of the dialog.
  boxes[0]?.focus();
  return dialog;
}
